import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { ManagedSFTPDirectory } from '@/modules/managedSFTP/ManagedSFTPDirectory';
import {
  ManagedSftpFilesDocument,
  ManagedSftpFilesQuery,
  ManagedSftpFilesQueryVariables,
} from '@/modules/managedSFTP/ManagedSFTPFiles.generated';

export function ManagedSFTPFilesPage() {
  const allowed = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MANAGE_SFTP
  );
  return allowed ? <AuthorizedFilesPage /> : null;
}

function AuthorizedFilesPage() {
  const client = useApolloClient();
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const loadChildren = useCallback(
    async (path: string) => {
      try {
        const { data } = await client.query<
          ManagedSftpFilesQuery,
          ManagedSftpFilesQueryVariables
        >({
          query: ManagedSftpFilesDocument,
          variables: { path },
          fetchPolicy: 'no-cache',
          errorPolicy: 'none',
        });
        trackUserEvent('integration sftp_directory_viewed', {
          root: path === '',
        });
        return data.managedSFTPFiles;
      } catch (error) {
        reportError('Could not list managed SFTP files', error as Error);
        throw error;
      }
    },
    [client, reportError, trackUserEvent]
  );
  return (
    <SubpageLayout
      heading="Managed SFTP files"
      description="Files received in your tenant’s SFTP landing directory. This view is read-only."
    >
      <ManagedSFTPDirectory loadChildren={loadChildren} />
    </SubpageLayout>
  );
}
